import { Fragment, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';
import { InlineError } from '../../ui/InlineError';
import { Skeleton } from '../../ui/Skeleton';
import { PageHeader } from '../shell/PageHeader';
import { useContainers } from '../../api/queries';
import { isRyptError } from '../../api/errors';
import { finalMessageFor } from '../../api/error-table';
import { containers as copy } from '../../copy/containers';
import { containerPath, sectionOf } from './paths';
import { rememberContainer } from './lastContainer';
import { RetryButton } from '../shell/RetryButton';

/**
 * Every page about a container's contents renders inside this.
 *
 * It checks the container in the URL is one of the tenant's; one that is not — deleted in
 * another tab, a stale bookmark — sends the viewer to the same page of the default
 * container rather than to a page about nothing. It remembers the container, so a tenant
 * page and the flat paths can come back to it. And it keys the page on the container, so
 * switching remounts it: Overview's latched mode, a half-filled dialog and every other
 * piece of page state belong to the container they were made in.
 */
export function ContainerLayout() {
  const { containerId = '' } = useParams();
  const { pathname } = useLocation();
  const containers = useContainers();
  const known = containers.data?.some((c) => c.id === containerId) ?? false;

  useEffect(() => {
    if (known) rememberContainer(containerId);
  }, [known, containerId]);

  // Data first: a refresh that fails over a list already read keeps the page, which says
  // for itself whether its own read is stale. Only a first read that failed stops here.
  if (containers.data === undefined) {
    if (!containers.error) return <Skeleton variant="block" lines={4} label={copy.finding} />;
    const error = isRyptError(containers.error) ? containers.error : null;
    // Under a heading, so a page that stops here still has an h1.
    return (
      <>
        <PageHeader title={copy.finding} />
        <InlineError
          title={copy.loadFailed}
          {...(error ? { requestId: error.requestId } : {})}
          action={<RetryButton onRetry={() => void containers.refetch()} />}
        >
          {error ? finalMessageFor(error) : String(containers.error)}
        </InlineError>
      </>
    );
  }
  if (!known) {
    const fallback = containers.data.find((c) => c.is_default) ?? containers.data[0];
    return fallback ? <Navigate to={containerPath(fallback.id, sectionOf(pathname) ?? 'overview')} replace /> : null;
  }
  return (
    <Fragment key={containerId}>
      <Outlet />
    </Fragment>
  );
}

export default ContainerLayout;
